import Icon from "@mui/material/Icon";
import profilePicture from "assets/images/profil-default.png";
import MDAvatar from "components/MDAvatar";
import i18next from "i18n";
import { logout } from "pages/auth/auth.hook";
import { lazy, Suspense, useEffect } from "react";
import { Features } from "types/client.type";
import { EFeatures } from "types/features.type";
import { Admin } from "types/admin.type";
import { Route } from "types/route.type";
import RolesType from "types/roles.type";
import { t } from "i18next";
import MDBox from "components/MDBox";
import { CircularProgress } from "@mui/material";

// Lazy loaded pages - These will be loaded on demand
const ResetCover = lazy(() => import("pages/auth/ResetPassword"));
const ResetPasswordCode = lazy(() => import("pages/auth/ResetPasswordCode"));
const SignIn = lazy(() => import("pages/auth/SignIn"));
const SignUp = lazy(() => import("pages/auth/SignUp"));
const VerifyEmail = lazy(() => import("pages/auth/VerifyEmail"));
const CreateApp = lazy(() => import("pages/create-app/pages/Create-app"));
const Dashboard = lazy(() => import("pages/dashboard"));
const Design = lazy(() => import("pages/design"));
const Element = lazy(() => import("pages/element"));
const AddProduct = lazy(() => import("pages/element/pages/AddProduct"));
const AppPublishing = lazy(() => import("pages/publishing"));
const AdminComponent = lazy(() => import("pages/admin"));
const EditProduct = lazy(() => import("pages/element/pages/EditProduct"));
const HopelappStore = lazy(() => import("pages/hopelappStore"));
const StripeCallback = lazy(() => import("pages/hopelappStore/components/Stripe/StripeCallback"));
const MyApp = lazy(() => import("pages/myApp"));
const TechnicalService = lazy(() => import("pages/technicalService"));
const ThankYou = lazy(() => import("pages/thank-you"));
const Chat = lazy(() => import("pages/chat"));
const ThemeDemo = lazy(() => import("pages/theme-demo"));
const EditResource = lazy(() => import("pages/resource/pages/EditResource"));
const AccountManagement = lazy(() => import("pages/accountManagement"));

// Loading fallback component
const PageLoader = () => (
  <MDBox
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px"
  >
    <CircularProgress color="info" />
  </MDBox>
);

// Helper to wrap lazy components with Suspense
const withSuspense = (Component: React.LazyExoticComponent<any>, props?: any) => (
  <Suspense fallback={<PageLoader />}>
    <Component {...props} />
  </Suspense>
);

// Interface pour l'utilisateur authentifié
interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  emailVerified: boolean;
  role: string[];
}

const createFeatureRoutes = (): Map<EFeatures, Route> => {
  const featureRoutes = new Map();

  featureRoutes.set(EFeatures.CHAT, {
    type: "collapse",
    noCollapse: true,
    icon: <Icon fontSize="medium">chat</Icon>,
    name: i18next.t("navbar.chat"),
    key: "chat",
    route: "/chat",
    component: withSuspense(Chat),
    privateRoute: true,
  });

  featureRoutes.set(EFeatures.ACCOUNT, {
    type: "collapse",
    noCollapse: true,
    icon: <Icon fontSize="medium">account_circle</Icon>,
    name: i18next.t("navbar.accountManagement"),
    key: "account",
    route: "/account",
    component: withSuspense(AccountManagement),
    privateRoute: true,
  });

  return featureRoutes;
};

const createMainRoutes = (isSuperAdmin: boolean): Route[] => [
  {
    type: "collapse",
    noCollapse: true,
    icon: <Icon fontSize="medium">feed</Icon>,
    name: i18next.t("navbar.myApp"),
    key: "myApp",
    route: "/myApp",
    component: withSuspense(MyApp),
    privateRoute: true,
  },
  {
    type: "collapse",
    noCollapse: true,
    icon: <Icon fontSize="medium">shopping_basket</Icon>,
    name: i18next.t("navbar.element"),
    key: "element",
    route: "/element",
    component: withSuspense(Element),
    privateRoute: true,
  },
  {
    noCollapse: true,
    icon: <Icon fontSize="medium">view_in_ar</Icon>,
    name: i18next.t("navbar.edit"),
    key: "element-edit",
    route: "/element/edit",
    component: withSuspense(EditProduct),
    privateRoute: true,
  },
  {
    noCollapse: true,
    icon: <Icon fontSize="medium">view_in_ar</Icon>,
    name: "Coucou",
    key: "resource-edit",
    route: "/resource/edit",
    component: withSuspense(EditResource),
    privateRoute: true,
  },
  {
    noCollapse: true,
    name: "resource",
    key: "resource",
    route: `/resource`,
    component: withSuspense(Element, { tab: "resource" }),
    privateRoute: true,
  },
  {
    noCollapse: true,
    icon: <Icon fontSize="medium">view_in_ar</Icon>,
    name: i18next.t("navbar.add"),
    key: "element-new",
    route: "/element/new",
    component: withSuspense(AddProduct),
    privateRoute: true,
  },
  {
    type: "collapse",
    noCollapse: true,
    icon: <Icon fontSize="medium">inbox</Icon>,
    name: i18next.t("navbar.dashboard"),
    key: "dashboard",
    route: "/dashboard",
    component: withSuspense(Dashboard),
    privateRoute: true,
  },
  {
    type: "collapse",
    noCollapse: true,
    icon: <Icon fontSize="medium">view_in_ar</Icon>,
    name: i18next.t("navbar.design"),
    key: "design",
    route: "/design",
    component: withSuspense(Design),
    privateRoute: true,
  },
  {
    type: "collapse",
    noCollapse: true,
    icon: <Icon fontSize="medium">widgets</Icon>,
    name: i18next.t("navbar.store"),
    key: "store",
    route: "/store",
    component: withSuspense(HopelappStore),
    privateRoute: true,
  },
  {
    noCollapse: true,
    icon: <Icon fontSize="medium">content_paste</Icon>,
    name: "Create App",
    key: "create-app",
    route: "/create/app",
    component: withSuspense(CreateApp),
    privateRoute: true,
  },
  {
    noCollapse: true,
    icon: <Icon fontSize="medium">content_paste</Icon>,
    name: "Stripe Callback",
    key: "stripe-callback",
    route: "/store/stripe-callback",
    component: withSuspense(StripeCallback),
    privateRoute: true,
  },
  {
    type: "collapse",
    noCollapse: true,
    icon: <Icon fontSize="medium">palette</Icon>,
    name: "Theme Demo",
    key: "theme-demo",
    route: "/theme-demo",
    component: withSuspense(ThemeDemo),
    privateRoute: true,
  },
];

const createAuthenticationRoutes = (): Route => ({
  name: "Authentication",
  key: "authentication",
  icon: <Icon fontSize="medium">content_paste</Icon>,
  collapse: [
    {
      name: "Sign In",
      key: "sign-in",
      collapse: [
        {
          name: "Basic",
          key: "basic",
          route: "/authentication/sign-in",
          component: withSuspense(SignIn),
        },
      ],
    },
    {
      name: "Sign Up",
      key: "sign-up",
      collapse: [
        {
          name: "Cover",
          key: "cover",
          route: "/authentication/sign-up",
          component: withSuspense(SignUp),
        },
      ],
    },
    {
      name: "Reset Password",
      key: "reset-password",
      collapse: [
        {
          name: "Cover",
          key: "cover",
          route: "/authentication/reset-password",
          component: withSuspense(ResetCover),
        },
        {
          name: "Reset Password Code",
          key: "reset-password-code",
          route: "/authentication/reset-password-code",
          component: withSuspense(ResetPasswordCode),
        },
      ],
    },
    {
      name: "Thank you",
      key: "thank-you",
      collapse: [
        {
          name: "Cover",
          key: "cover",
          route: "/thank-you",
          component: withSuspense(ThankYou),
        },
      ],
    },
    {
      name: "Verify email",
      key: "verify-email",
      collapse: [
        {
          name: "Cover",
          key: "cover",
          route: "/authentication/verify-email",
          component: withSuspense(VerifyEmail),
        },
      ],
    },
  ],
});

const createConfigurableFeatures = (isSuperAdmin: boolean): Route[] => {
  return [
    ...(isSuperAdmin
      ? [
          {
            type: "collapse" as const,
            noCollapse: true,
            icon: <Icon fontSize="medium">rocket_launch</Icon>,
            name: i18next.t("navbar.application-publishing"),
            key: "application-publishing",
            route: "/application-publishing",
            component: withSuspense(AppPublishing),
            privateRoute: true,
          },
          {
            type: "collapse" as const,
            noCollapse: true,
            icon: <Icon fontSize="medium">admin_panel_settings</Icon>,
            name: i18next.t("navbar.admin"),
            key: "admin",
            route: "/admin",
            component: withSuspense(AdminComponent),
            privateRoute: true,
          },
        ]
      : []),
    {
      type: "collapse",
      noCollapse: true,
      icon: <Icon fontSize="medium">live_help</Icon>,
      name: i18next.t("navbar.technical-support"),
      key: "technical-support",
      route: "/deployment",
      component: withSuspense(TechnicalService),
      privateRoute: true,
    },
  ];
};

const routes = (currentAdmin?: Admin, features?: Features): Route[] => {
  const isSuperAdmin = currentAdmin?.role?.includes(RolesType.SUPER_ADMIN) || false;
  const featureRoutes = createFeatureRoutes();

  const enabledFeatures = features
    ? Object.entries(features)
        .filter(([key, value]) => value.enabled && featureRoutes.has(key as EFeatures))
        .map(([key]) => featureRoutes.get(key as EFeatures))
    : [];

  return [
    {
      type: "collapse",
      name: currentAdmin?.lastName,
      key: "user-profile",
      icon: <MDAvatar src={profilePicture} alt={currentAdmin?.lastName || "User"} size="sm" />,
      collapse: [
        {
          name: i18next.t("navbar.logout"),
          key: "logout",
          route: "/logout",
          component: (() => {
            const LogoutComponent = () => {
              useEffect(() => {
                logout();
              }, []);
              return <div>Déconnexion en cours...</div>;
            };
            return <LogoutComponent />;
          })(),
        },
      ],
    },
    { type: "divider", key: "divider-0" },
    { type: "title", title: "Pages", key: "title-pages" },
    ...createMainRoutes(isSuperAdmin),
    { type: "divider", key: "divider-1" },
    ...enabledFeatures,
    ...createConfigurableFeatures(isSuperAdmin),
    createAuthenticationRoutes(),
  ];
};

export default routes;
